import React, { useContext, useMemo, useState } from "react";
import Modal from "./Modal";
import Button from "./Button";
import UserManual from "./UserManual/UserManual";
import { ModelTsContext } from "../context/modelTS.context";
import { canAccessTimeMachine } from "../utility/canAccessTimeMachine";
import leelaBg from "../assets/leela-help-bg.jpg";

// - - - - - - - - - -
// Hardcoded list of training videos. To add new videos, append to the
// appropriate section array. Newest entries first within each section.

const SCHEDULER_VIDEOS = [
  {
    version: "v04-28-2026-01",
    title: "resizable RTS, multiple resizable scheduler grids",
    directorNotes: "Leela demos, Oliver observes, Michael unplugs demo",
    url: "https://s3.us-east-1.amazonaws.com/portal.kellerinteriors.com/Scheduler_v04-28-2026.mp4",
  },
  {
    version: "v02-25-2026-01",
    title: "minor fixes, dashed cell borders, un-park, created section",
    directorNotes: "Leela demos, Oliver observes, Michael tosses acorns during credits",
    url: "https://s3.us-east-1.amazonaws.com/portal.kellerinteriors.com/Scheduler_02_25_26.mp4",
  },
  {
    version: "v02-04-2026-01",
    title: "installer compliance, unschedule suppression, parking",
    directorNotes: "Leela demos, mentions that nephew Michael is throwing acorns at neighbor's cat again",
    url: "https://s3.us-east-1.amazonaws.com/portal.kellerinteriors.com/Scheduler_parking.mp4",
  },
];

const FLOORING_XP_VIDEOS = [
  {
    version: "v1.0.1",
    title: "no User changes, data collection prep for v2.0",
    directorNotes: "Leela monologues, Michael spills acorns, drags cables, face blocks camera, hangs from window",
    url: "https://s3.us-east-1.amazonaws.com/portal.kellerinteriors.com/FlooringXP_v1.0.1.mp4",
  },
];

// - - - - - - - - - -

const VideoLink = ({ video }) => (
  <li className="py-2 border-b border-skin-base last:border-b-0">
    <a
      href={video.url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-skin-link hover:underline"
    >
      <span className="font-bold">{video.version}</span>
      <span className="ml-2">— {video.title}</span>
    </a>
    {video.directorNotes && (
      <div className="mt-0.5 ml-1 text-sm italic opacity-70">
        Director's notes: {video.directorNotes}
      </div>
    )}
  </li>
);

// - - - - - - - - - -

const TabButton = ({ tabId, activeTab, onSelect, children }) => {
  const isActive = activeTab === tabId;
  return (
    <button
      type="button"
      role="tab"
      id={`help-tab-${tabId}`}
      aria-selected={isActive}
      aria-controls={`help-panel-${tabId}`}
      onClick={() => onSelect(tabId)}
      className={`px-4 py-2 text-sm font-medium border-b-2 ${
        isActive
          ? "border-yellow text-skin-link"
          : "border-transparent opacity-70 hover:opacity-100"
      }`}
    >
      {children}
    </button>
  );
};

// - - - - - - - - - -

const TAB_VIDEOS = "videos";
const TAB_MANUAL = "manual";

function HelpModal({ setOpenHelpModal }) {
  const [activeTab, setActiveTab] = useState(TAB_VIDEOS);
  // Mirror the header's 🕒 button gate: when the logged-in user does not
  // have timeTravelAccess, the entire Time Travel chapter is hidden from
  // the User Manual nav too. Showing a chapter for a feature the user
  // cannot reach is just confusing.
  //
  // Defensive `|| {}` — useContext returns undefined when no provider
  // wraps HelpModal (test isolation, or a future call site that
  // mounts HelpModal outside the normal tree). Gate stays closed in
  // that case (no user → no access).
  const { mongoUser, tsUserDocument } = useContext(ModelTsContext) || {};
  const hiddenManualGroups = useMemo(
    () =>
      canAccessTimeMachine(mongoUser, tsUserDocument) ? [] : ["time-travel"],
    [mongoUser, tsUserDocument]
  );

  return (
    <Modal onClose={() => setOpenHelpModal({ show: false })}>
      <Modal.Header>Leela's Help</Modal.Header>
      <Modal.Content>
        <div className="relative min-w-[56rem] min-h-[28rem]">
          <div
            className="absolute inset-0 pointer-events-none bg-center bg-cover opacity-20"
            style={{ backgroundImage: `url(${leelaBg})` }}
          />
          <div className="relative flex flex-col p-6">
            <div
              role="tablist"
              aria-label="Help sections"
              className="flex gap-2 border-b border-skin-base mb-4"
            >
              <TabButton
                tabId={TAB_VIDEOS}
                activeTab={activeTab}
                onSelect={setActiveTab}
              >
                Training Videos
              </TabButton>
              <TabButton
                tabId={TAB_MANUAL}
                activeTab={activeTab}
                onSelect={setActiveTab}
              >
                User Manual
              </TabButton>
            </div>

            {activeTab === TAB_VIDEOS && (
              <div
                role="tabpanel"
                id={`help-panel-${TAB_VIDEOS}`}
                aria-labelledby={`help-tab-${TAB_VIDEOS}`}
                className="flex flex-col gap-6"
              >
                <section>
                  <div className="text-lg font-bold mb-2">Scheduler</div>
                  <ul>
                    {SCHEDULER_VIDEOS.map((video) => (
                      <VideoLink key={video.url} video={video} />
                    ))}
                  </ul>
                </section>
                <section>
                  <div className="text-lg font-bold mb-2">Flooring XP</div>
                  <ul>
                    {FLOORING_XP_VIDEOS.map((video) => (
                      <VideoLink key={video.url} video={video} />
                    ))}
                  </ul>
                </section>
              </div>
            )}

            {activeTab === TAB_MANUAL && (
              <div
                role="tabpanel"
                id={`help-panel-${TAB_MANUAL}`}
                aria-labelledby={`help-tab-${TAB_MANUAL}`}
              >
                <UserManual hiddenGroups={hiddenManualGroups} />
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className="h-8 flex gap-4">
          <Button
            color="light"
            className="uppercase w-31"
            onClick={() => setOpenHelpModal({ show: false })}
          >
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default HelpModal;
